import { IFormJBPHPContactChangeRequest } from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { ISelectPartnerDialogItem } from 'legacy/lut/components/widgets/public/salesTeam/components/dialogs/selectPartnerDialog/models'
import { useCallback, useState } from 'react'

import { usePrefilledChangeRequest } from 'partner360/features/welcomeFlow/model/hooks/usePrefilledChangeRequest'
import { useWelcomeFlowApi } from 'partner360/features/welcomeFlow/model/hooks/useWelcomeFlowApi'
import { IWelcomeFlowPartner } from 'partner360/features/welcomeFlow/model/types'

/** The change-request flow of the Welcome Flow's partner list: pick a partner, edit, apply. */
export const useMyPartnersRequests = (onPartnersChanged?: () => void) => {
  const [isApplyingRequest, setIsApplyingRequest] = useState(false)
  const [isRequestEditorVisible, setIsRequestEditorVisible] = useState(false)
  const [isSelectPartnerDialogVisible, setIsSelectPartnerDialogVisible] = useState(false)

  const { createChangeRequest, removeChangeRequest } = useWelcomeFlowApi()
  const {
    changeRequest,
    createPrefilledAddRequest,
    createPrefilledRemoveRequest,
    createPrefilledUpdateRequest,
    resetPrefilledChangeRequest,
  } = usePrefilledChangeRequest()

  const closeRequestEditor = useCallback(() => {
    setIsRequestEditorVisible(false)
    resetPrefilledChangeRequest()
  }, [resetPrefilledChangeRequest])

  const applyRequest = useCallback(
    async (request: IFormJBPHPContactChangeRequest) => {
      setIsApplyingRequest(true)
      closeRequestEditor()

      try {
        await createChangeRequest(request)
        onPartnersChanged?.()
      } finally {
        setIsApplyingRequest(false)
      }
    },
    [closeRequestEditor, createChangeRequest, onPartnersChanged]
  )

  const removeRequest = useCallback(
    async (requestId: number) => {
      setIsApplyingRequest(true)
      closeRequestEditor()

      try {
        await removeChangeRequest(requestId)
        onPartnersChanged?.()
      } finally {
        setIsApplyingRequest(false)
      }
    },
    [closeRequestEditor, removeChangeRequest, onPartnersChanged]
  )

  const openRemoveRequest = useCallback(
    async (partner: IWelcomeFlowPartner) => {
      createPrefilledRemoveRequest(partner)
      setIsRequestEditorVisible(true)
    },
    [createPrefilledRemoveRequest]
  )

  const openUpdateRequest = useCallback(
    async (partner: IWelcomeFlowPartner) => {
      createPrefilledUpdateRequest(partner)
      setIsRequestEditorVisible(true)
    },
    [createPrefilledUpdateRequest]
  )

  const openSelectPartnerDialog = useCallback(() => {
    setIsSelectPartnerDialogVisible(true)
  }, [])

  const closeSelectPartnerDialog = useCallback(() => {
    setIsSelectPartnerDialogVisible(false)
  }, [])

  const selectPartner = useCallback(
    (partner: ISelectPartnerDialogItem) => {
      createPrefilledAddRequest(partner.partnerId, partner.communicationName)
      setIsRequestEditorVisible(true)
    },
    [createPrefilledAddRequest]
  )

  return {
    changeRequest,
    isApplyingRequest,
    isRequestEditorVisible,
    isSelectPartnerDialogVisible,
    applyRequest,
    removeRequest,
    openRemoveRequest,
    openUpdateRequest,
    openSelectPartnerDialog,
    closeSelectPartnerDialog,
    selectPartner,
    closeRequestEditor,
  }
}
